// src/oura/components/client/ClientDialogs.tsx
//
// The two setup dialogs the Client screen opens from its More menu: upload a
// report for the merchant, and create the merchant's login. Moved here
// unchanged from the Client screen.

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { useSnackbar } from "@hook/use-snackbar";
import { Button } from "../../primitives/Button";
import { reportTitle, titleFromFileName } from "../../lib/reportTitle";
import { createClientUser, uploadReport, REPORT_SECTIONS_MAX, REPORT_SECTION_MAX_CHARS, UPLOAD_MAX_BYTES } from "@service/aeo/clientReview";

/**
 * The staff report a merchant opens from their Reports tab (#3674 / backend T12).
 *
 * PDF only, because that is what the server takes and what a merchant can open
 * on a phone without an app. The title is required: a list of files named
 * "report-final-2.pdf" is not a list a client can read.
 */
export function UploadReportDialog({
    open,
    businessId,
    onOpenChange,
    onUploaded,
    notify = true,
}: {
    open: boolean;
    businessId: string;
    onOpenChange: (v: boolean) => void;
    /**
     * The Reports card reads again: the result of an upload belongs on the tab it was started from (#4009).
     * Awaited: the dialog stays busy until it settles, so the caller finishes with this upload before another can start.
     */
    onUploaded?: () => void | Promise<void>;
    /** false: the client is not told about this upload (the results card sends one email for both, #4398). */
    notify?: boolean;
}) {
    const snackbar = useSnackbar();
    const [file, setFile] = useState<File | null>(null);
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    /** One heading per line, as typed. Split and checked at submit. */
    const [sections, setSections] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => { setFile(null); setTitle(""); setSummary(""); setSections(""); setError(null); };
    // Every exit clears the form, so reopening never shows the last attempt.
    // A person cannot close it while the upload is out: the old request's
    // success would otherwise close and clear a dialog opened again since.
    const finishClose = () => { reset(); onOpenChange(false); };
    const handleOpenChange = (next: boolean) => {
        if (next) onOpenChange(true);
        else if (!busy) finishClose();
    };
    // The title that goes: what was typed, else the file's name as a title. The
    // file's name is NOT put into the box (#4031): a person typing into it ran
    // their title into the file name with no space between.
    const sentTitle = reportTitle(title, file?.name ?? null);
    const canSubmit = !!file && sentTitle.length > 0 && !busy;

    const pick = (f: File | undefined) => {
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".pdf")) { setError("Reports are PDFs."); return; }
        if (f.size > UPLOAD_MAX_BYTES) { setError("That file is over 20 MB."); return; }
        setError(null);
        setFile(f);
    };

    const submit = async () => {
        if (!canSubmit || !file) return;
        // The server's own two limits, said here so a refused upload does not
        // cost a 20 MB round trip -- and in words, never its `invalid_sections`
        // code.
        const headings = sections.split("\n").map((x) => x.trim()).filter(Boolean);
        if (headings.length > REPORT_SECTIONS_MAX) {
            setError(`At most ${REPORT_SECTIONS_MAX} headings. Take ${headings.length - REPORT_SECTIONS_MAX} out.`);
            return;
        }
        const tooLong = headings.find((x) => x.length > REPORT_SECTION_MAX_CHARS);
        if (tooLong) {
            setError(`A heading is at most ${REPORT_SECTION_MAX_CHARS} characters. Shorten "${tooLong.slice(0, 30)}…".`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await uploadReport(businessId, file, sentTitle, summary.trim(), headings, notify);
            snackbar.showSuccess("Report uploaded", "It is on the Reports card here, and on the merchant's Reports tab.");
            // The file is in by now: a failure in the caller is the caller's to show, never "not uploaded".
            await Promise.resolve(onUploaded?.()).catch(() => undefined);
            finishClose();
        } catch {
            setError("The report was not uploaded. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Upload a report</DialogTitle>
                    <DialogDescription>
                        A PDF the merchant can open and download from their Reports tab.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-report-file" className="text-sm font-medium">File</Label>
                        <Input id="oura-report-file" type="file" accept=".pdf" onChange={(e) => pick(e.target.files?.[0])} />
                        {file && <p className="text-xs text-gray-500">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</p>}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-report-title" className="text-sm font-medium">Title</Label>
                        <Input
                            id="oura-report-title"
                            value={title}
                            placeholder={file ? titleFromFileName(file.name) : "e.g. September visibility report"}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {file && !title.trim() && (
                            <p className="text-xs text-gray-500">Left empty, the title is the file&rsquo;s name: &ldquo;{sentTitle}&rdquo;.</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-report-summary" className="text-sm font-medium">
                            One line <span className="font-normal text-gray-400">(optional)</span>
                        </Label>
                        <Input id="oura-report-summary" value={summary} placeholder="What it says, in a sentence" onChange={(e) => setSummary(e.target.value)} />
                    </div>
                    {/* The headings inside, which the server has accepted since
                        #5112 and nothing ever sent -- so every uploaded report
                        stored none and the client's drawer never showed
                        "Inside" (#4026 C-FE-8). */}
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-report-sections" className="text-sm font-medium">
                            What&rsquo;s inside <span className="font-normal text-gray-400">(optional)</span>
                        </Label>
                        <textarea
                            id="oura-report-sections"
                            value={sections}
                            rows={4}
                            placeholder={"One heading per line, e.g.\nWhat moved this month\nWhere you are named\nWhat we are doing next"}
                            onChange={(e) => { setSections(e.target.value); setError(null); }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500">
                            {/* True only since C-BE-2, which is on staging: an
                                upload with none now takes the PDF's own
                                top-level bookmarks. */}
                            Leave empty to use the PDF&rsquo;s own headings. At most {REPORT_SECTIONS_MAX}, {REPORT_SECTION_MAX_CHARS} characters each.
                        </p>
                    </div>
                    {error && <p className="text-xs text-rose-600">{error}</p>}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={finishClose} disabled={busy}>Cancel</Button>
                    <Button disabled={!canSubmit} onClick={() => void submit()}>{busy ? "Uploading…" : "Upload"}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Create the merchant's login from the business (#3671 / backend T1).
 *
 * The server creates the client workspace if the business has none, issues the
 * user as `oura-client`, and sends the set-password email, so the staffer never
 * handles a password and there is nothing to copy out of this dialog.
 */
export function CreateLoginDialog({ open, businessId, onOpenChange }: { open: boolean; businessId: string; onOpenChange: (v: boolean) => void }) {
    const snackbar = useSnackbar();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Checked here so an obviously bad address never becomes a request. The
    // server is still the authority on whether the email is usable.
    const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const canSubmit = name.trim().length > 0 && emailLooksValid && !busy;

    const reset = () => { setName(""); setEmail(""); setError(null); };
    // Same rule as the upload: no closing while the request is out.
    const finishClose = () => { reset(); onOpenChange(false); };
    const handleOpenChange = (next: boolean) => {
        if (next) onOpenChange(true);
        else if (!busy) finishClose();
    };

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        // try/finally, not a bare await: anything that throws on the way (and
        // the service's own error reporting is on that path) would otherwise
        // leave the button reading "Sending…" for good, with nothing on screen
        // saying the request is over.
        try {
            const r = await createClientUser(businessId, { name: name.trim(), email: email.trim().toLowerCase() });
            if (r.ok) {
                snackbar.showSuccess("Login sent", `They can set a password from the email at ${r.email}.`);
                finishClose();
                return;
            }
            // A conflict is the staffer's problem to solve, so the dialog stays
            // open with what they typed still in it.
            setError(r.message);
        } catch {
            setError("The login could not be created. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Create merchant login</DialogTitle>
                    <DialogDescription>
                        They get an email to set their own password, then see their Home,
                        Decisions and Reports. Nothing is shown here to copy.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-client-name" className="text-sm font-medium">Name</Label>
                        <Input id="oura-client-name" autoFocus value={name} placeholder="e.g. Wei Ling" onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="oura-client-email" className="text-sm font-medium">Email</Label>
                        <Input id="oura-client-email" type="email" value={email} placeholder="name@business.com" onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    {error && <p className="text-xs text-rose-600">{error}</p>}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={finishClose} disabled={busy}>Cancel</Button>
                    <Button disabled={!canSubmit} onClick={() => void submit()}>{busy ? "Sending…" : "Send login"}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
